import type { ChangeEvent } from "react";
import { action, computed, makeObservable, observable } from "mobx";
import { injectable } from "tsyringe";
import { ViewModel } from "../../lib/vvm";
import type { InputOnChangeData } from "../../components/Input/Input";
import { generateQrCodesBatch } from "./viewmodel/batch";
import { buildQrPrintHtml, type QrPrintLayout } from "./viewmodel/print-export";
import type { ManagedQrCode } from "./viewmodel/types";

const MIN_GENERATION_COUNT = 1;
const MAX_GENERATION_COUNT = 2400;

export interface QrA4Preset extends QrPrintLayout {
  key: string;
}

const QR_A4_PRESETS: readonly QrA4Preset[] = [
  {
    key: "a4_1",
    label: "A4 (1 код на лист, максимум размера)",
    codesPerSheet: 1,
    columns: 1,
    rows: 1,
  },
  {
    key: "a4_4",
    label: "A4 / 4 (2x2, очень крупный)",
    codesPerSheet: 4,
    columns: 2,
    rows: 2,
  },
  {
    key: "a4_6",
    label: "A4 / 6 (2x3, крупный)",
    codesPerSheet: 6,
    columns: 2,
    rows: 3,
  },
  {
    key: "a4_12",
    label: "A4 / 12 (3x4, средний)",
    codesPerSheet: 12,
    columns: 3,
    rows: 4,
  },
  {
    key: "a4_24",
    label: "A4 / 24 (4x6, компактный)",
    codesPerSheet: 24,
    columns: 4,
    rows: 6,
  },
];

@injectable()
export class QRManagementPageViewModel extends ViewModel {
  @observable.shallow
  codes: ManagedQrCode[] = [];

  @observable
  requestedCountInput = "24";

  @observable
  selectedPresetKey: string = QR_A4_PRESETS[2].key;

  @observable
  generatedPresetKey: string | null = null;

  @observable
  operationError: string | null = null;

  @observable
  operationMessage: string | null = null;

  @observable
  isBusy = false;

  constructor() {
    super();
    makeObservable(this);
  }

  @computed
  get sheetPresetOptions(): readonly QrA4Preset[] {
    return QR_A4_PRESETS;
  }

  @computed
  get selectedPreset(): QrA4Preset {
    return (
      QR_A4_PRESETS.find((preset) => preset.key === this.selectedPresetKey) ??
      QR_A4_PRESETS[0]
    );
  }

  @computed
  get exportPreset(): QrA4Preset {
    if (!this.generatedPresetKey) {
      return this.selectedPreset;
    }
    return (
      QR_A4_PRESETS.find((preset) => preset.key === this.generatedPresetKey) ??
      this.selectedPreset
    );
  }

  @computed
  get requestedCount(): number | null {
    const parsed = Number(this.requestedCountInput);
    if (!Number.isInteger(parsed) || parsed < MIN_GENERATION_COUNT) {
      return null;
    }
    return parsed;
  }

  @computed
  get normalizedGenerationCount(): number {
    const requested = this.requestedCount;
    if (requested === null) {
      return 0;
    }
    const perSheet = this.selectedPreset.codesPerSheet;
    return Math.ceil(requested / perSheet) * perSheet;
  }

  @computed
  get generationSheetsCount(): number {
    const normalized = this.normalizedGenerationCount;
    if (normalized <= 0) {
      return 0;
    }
    return normalized / this.selectedPreset.codesPerSheet;
  }

  @computed
  get totalCount(): number {
    return this.codes.length;
  }

  @computed
  get exportSheetsCount(): number {
    if (this.codes.length === 0) {
      return 0;
    }
    return Math.ceil(this.codes.length / this.exportPreset.codesPerSheet);
  }

  @computed
  get canGenerateBatch(): boolean {
    return (
      !this.isBusy &&
      this.normalizedGenerationCount >= MIN_GENERATION_COUNT &&
      this.normalizedGenerationCount <= MAX_GENERATION_COUNT
    );
  }

  @computed
  get canExport(): boolean {
    return !this.isBusy && this.codes.length > 0;
  }

  qrImageUrl(payload: string, size = 620): string {
    const params = new URLSearchParams({
      size: `${size}x${size}`,
      data: payload,
      ecc: "H",
      margin: "12",
    });
    return `https://api.qrserver.com/v1/create-qr-code/?${params.toString()}`;
  }

  @action.bound
  onRequestedCountChange(
    _event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ): void {
    this.requestedCountInput = data.value;
    this.operationError = null;
  }

  @action.bound
  onSheetPresetChange(event: ChangeEvent<HTMLSelectElement>): void {
    this.selectedPresetKey = event.target.value;
    this.operationError = null;
  }

  @action.bound
  generateBatch(): void {
    const requested = this.requestedCount;
    if (requested === null) {
      this.operationError = "Укажите целое количество QR-кодов (минимум 1).";
      this.operationMessage = null;
      return;
    }

    const normalized = this.normalizedGenerationCount;
    if (normalized > MAX_GENERATION_COUNT) {
      this.operationError =
        `С учетом кратности формата получится ${normalized}. ` +
        `Это больше лимита ${MAX_GENERATION_COUNT}. Уменьшите количество.`;
      this.operationMessage = null;
      return;
    }

    const generated = generateQrCodesBatch(normalized);
    this.codes = generated;
    this.generatedPresetKey = this.selectedPreset.key;
    this.operationError = null;

    const roundedHint =
      normalized === requested
        ? ""
        : ` (округлено до кратности ${this.selectedPreset.codesPerSheet})`;

    this.operationMessage =
      `Сгенерировано ${generated.length} новых QR-кодов: ` +
      `${this.selectedPreset.label}, ${this.generationSheetsCount} лист(ов)${roundedHint}.`;
  }

  @action.bound
  exportAsPdf(): void {
    if (!this.canExport) {
      this.operationError = "Сначала сгенерируйте партию QR-кодов.";
      this.operationMessage = null;
      return;
    }

    const printWindow = window.open("", "_blank", "width=1080,height=900");
    if (!printWindow) {
      this.operationError = "Не удалось открыть окно печати.";
      this.operationMessage = null;
      return;
    }

    const preset = this.exportPreset;
    const html = buildQrPrintHtml({
      codes: this.codes,
      qrImageUrl: (payload, size) => this.qrImageUrl(payload, size),
      layout: {
        label: preset.label,
        codesPerSheet: preset.codesPerSheet,
        columns: preset.columns,
        rows: preset.rows,
      },
    });

    printWindow.document.write(html);
    printWindow.document.close();

    this.operationError = null;
    this.operationMessage =
      `Открыт режим печати: ${preset.label}, ` +
      `${this.exportSheetsCount} лист(ов), ${this.totalCount} кодов.`;
  }
}
